/** @format */

import React, { useEffect, useState } from 'react';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import PaperedTable from '../../../components/Table/PaperedTable';
import { TableBody, TableCell, TableFooter, TableHead, TableRow } from '@material-ui/core';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import CancelButton from '../../../components/Buttons/CancelButton';
import Pager from '../../../components/Adescom/Pager';
import Filter from '../../../interfaces/forms/Filter';
import Order from '../../../interfaces/forms/Order';
import { makeStyles } from '@material-ui/core/styles';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import { FilterReducerFieldName } from '../../../modules/filter/FilterReducers';
import { FilterListReducerFieldName } from '../../../modules/filter/FilterListReducers';
import { useDispatch } from 'react-redux';
import { usePrefixUploadCollection } from '../../../modules/prefixes/hooks/usePrefixUploadCollection';
import I18n from '../../../components/Translate/I18n';
import TemplateButton from '../../../components/Buttons/TemplateButton';
import { Report } from '@material-ui/icons';
import { getPrefixUploadFileStatus, PREFIX_UPLOAD_STATUS } from '../../../modules/prefixes/helpers/PrefixUploadHelper';
import { DateHelperTs } from '../../../helpers/DateHelperTs';
import { PrefixUploadInterface } from '../../../modules/prefixes/interfaces/PrefixUploadInterface';
import GeneralDialog from '../../../components/Dialog/GeneralDialog';
import PrefixUploadReportView from './PrefixUploadReportView';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import FilterList from '../../../components/Filter/FilterList';

interface UploadPrefixFileListInterface {
    reload: boolean;
    setReload: (reload: boolean) => void;
}
const UploadPrefixFileList: React.FC<UploadPrefixFileListInterface> = (props) => {
    const { reload, setReload } = props;
    const [filters, setFilters] = useState<Array<Filter>>([]);
    const [orders, setOrders] = useState<Array<Order>>([{ name: 'importByDate', value: 'DESC' }]);
    const [selectedPrefixUploadFile, setSelectedPrefixUploadFile] = useState<PrefixUploadInterface | null>(null);

    const { prefixUploadCollection, state, refreshCollectionCallback, getCollectionCallback } = usePrefixUploadCollection(filters, orders);

    const dispatch = useDispatch();
    const useStyle = makeStyles(adescomStyle);
    const classes = useStyle();

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableStickyCommand = { root: classes.tableStickyCommand };
    const tableCellRoot = { root: classes.tableCellRootBordered };

    const updateOrderList = (orders: Array<Order>): void => setOrders(orders);
    const updateFilterList = (filters: Array<Filter>): void => setFilters(filters);

    const clearFilter = (): void => {
        dispatch({
            type: FilterReducerFieldName.HEADER_FILTER_MANAGE,
            clearAll: true,
        });
        dispatch({
            type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
        });
    };

    const setDefaultFilter = (callback: any): void => {
        setOrders([]);
        setFilters([]);
        callback();
    };

    const paginationCallbackFunction = (uri: string): void => {
        getCollectionCallback(uri);
    };

    const viewReport = (prefixUploadFile: PrefixUploadInterface): void => {
        if ([PREFIX_UPLOAD_STATUS.STATUS_IMPORT_AWAIT, PREFIX_UPLOAD_STATUS.STATUS_IMPORT_PROCESS].includes(prefixUploadFile.status)) {
            return;
        }

        setSelectedPrefixUploadFile(prefixUploadFile);
    };

    const closeReportView = (): void => {
        setSelectedPrefixUploadFile(null);
    };

    useEffect(() => {
        if (reload) {
            refreshCollectionCallback();
        }
    }, [reload, refreshCollectionCallback]);

    return (
        <GridContainer spacing={2}>
            <GridItem xs={12} md={12} lg={12}>
                <PaperedTable
                    beforeTableContent={
                        <GridContainer>
                            <GridItem xs={12} className={classes.beforeTableContent}>
                                <CustomTextButtom
                                    callback={refreshCollectionCallback}
                                    callbackParam={{}}
                                    color="primary"
                                    label="general:common.search"
                                    size="lg"
                                />
                                {(filters.length > 0 || orders.length > 0) && (
                                    <CustomTextButtom
                                        callback={(): void => setDefaultFilter(clearFilter)}
                                        callbackParam={{}}
                                        color="primary"
                                        label="general:common.clear"
                                        size="lg"
                                    />
                                )}
                                <FilterList />
                            </GridItem>
                        </GridContainer>
                    }
                >
                    <TableHead>
                        <TableRow>
                            <TableCell classes={tableStickyCommand} padding="none" align="left">
                                <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true} />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prefixes:upload.file_name"
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    fieldName="fileName"
                                    showSort={true}
                                    defaultDirection="desc"
                                    searchFieldType="string"
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prefixes:upload.status"
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    fieldName="status"
                                    showSort={true}
                                    defaultDirection="desc"
                                    searchFieldType="select"
                                    selectComponent="headerCheckBoxSimpleList"
                                    elementList={[
                                        { name: 'prefixes:upload.status_uploaded', value: PREFIX_UPLOAD_STATUS.STATUS_IMPORT_AWAIT },
                                        { name: 'prefixes:upload.status_import_process', value: PREFIX_UPLOAD_STATUS.STATUS_IMPORT_PROCESS },
                                        { name: 'prefixes:upload.status_imported', value: PREFIX_UPLOAD_STATUS.STATUS_IMPORT_FINISH },
                                        { name: 'prefixes:upload.status_error', value: PREFIX_UPLOAD_STATUS.STATUS_ERROR },
                                    ]}
                                    filters={filters}
                                    sort={orders}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prefixes:upload.import_by_date"
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    fieldName="importByDate"
                                    defaultDirection="desc"
                                    filters={filters}
                                    sort={orders}
                                    showSort={true}
                                    onlyFilter={false}
                                    searchFieldType="date"
                                    dateFormat="RRRR-MM-DD"
                                    beforeSetToMax={true}
                                    afterSetToMin={true}
                                    dateFromTo={true}
                                    breakLine={true}
                                    timeFormat={false}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="prefixes:upload.import_date"
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    fieldName="importDate"
                                    defaultDirection="desc"
                                    filters={filters}
                                    sort={orders}
                                    showSort={true}
                                    onlyFilter={false}
                                    searchFieldType="date"
                                    dateFormat="RRRR-MM-DD"
                                    beforeSetToMax={true}
                                    afterSetToMin={true}
                                    dateFromTo={true}
                                    breakLine={true}
                                    timeFormat={false}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {prefixUploadCollection &&
                            prefixUploadCollection['hydra:member'].map((prefixUploadFile) => (
                                <TableRow key={prefixUploadFile.id}>
                                    <TableCell classes={tableCellRoot} padding="none" align="left">
                                        <TemplateButton
                                            icon={Report}
                                            callback={viewReport}
                                            callbackParam={prefixUploadFile}
                                            tooltipTitle={
                                                [PREFIX_UPLOAD_STATUS.STATUS_IMPORT_AWAIT, PREFIX_UPLOAD_STATUS.STATUS_IMPORT_PROCESS].includes(
                                                    prefixUploadFile.status
                                                )
                                                    ? 'prefixes:upload.report_view_disabled'
                                                    : 'prefixes:upload.report_view_enabled'
                                            }
                                        />
                                    </TableCell>
                                    <TableCell classes={tableCellRoot} padding="none" align="left">
                                        {prefixUploadFile.fileName}
                                    </TableCell>
                                    <TableCell classes={tableCellRoot} padding="none" align="left">
                                        <I18n trans={getPrefixUploadFileStatus(prefixUploadFile.status)} />
                                    </TableCell>
                                    <TableCell classes={tableCellRoot} padding="none" align="left">
                                        {DateHelperTs.format(prefixUploadFile.importByDate)}
                                    </TableCell>
                                    <TableCell classes={tableCellRoot} padding="none" align="left">
                                        {prefixUploadFile.importDate && DateHelperTs.format(prefixUploadFile.importDate)}
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell classes={tableCellRoot} colSpan={4}>
                                {prefixUploadCollection && prefixUploadCollection['hydra:view'] && (
                                    <Pager callback={paginationCallbackFunction} views={prefixUploadCollection['hydra:view']} />
                                )}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </PaperedTable>
            </GridItem>
            <GridItem xs={12}>
                <PrefixUploadReportView prefixUploadFile={selectedPrefixUploadFile} closeReportView={closeReportView} />
            </GridItem>
        </GridContainer>
    );
};

export default UploadPrefixFileList;
